import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

interface User {
  userId: string
  email: string
  userType:
    | 'SuperAdmin'
    | 'CustomerService'
    | 'SalesMarketing'
    | 'ChannelPartner'
    | 'DealSponsor'
    | 'ComplianceOfficer'
  partnerId?: string
  schoolId?: string
  schoolName?: string
  schoolColors?: {
    primary: string
    secondary: string
  }
  piiAccess?: 'full' | 'partial' | 'none'
  dataScope?: string
}

interface SchoolBranding {
  institutionId: string
  name: string
  primaryColor: string
  secondaryColor: string
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const token = ref<string | null>(null)
  const isInitialized = ref(false)

  // Preview mode state (SUPERADMIN only)
  const previewRole = ref<string | null>(null)
  const previewBranding = ref<SchoolBranding | null>(null)
  const isPreviewMode = computed(() => !!previewRole.value || !!previewBranding.value)

  const isAuthenticated = computed(() => !!user.value && !!token.value)

  // Effective role (actual or preview)
  const effectiveRole = computed(() => previewRole.value || user.value?.userType || null)
  const userRole = computed(() => user.value?.userType || null)

  /**
   * Fetch current user from backend (Entra SSO session)
   */
  async function fetchCurrentUser(): Promise<void> {
    try {
      const response = await fetch('/api/admin/users/me', {
        credentials: 'include'
      })

      if (!response.ok) {
        throw new Error('Not authenticated')
      }

      const userData = await response.json()
      user.value = userData
      token.value = 'from-cookie'

      // Apply school colors if available
      applyBranding(userData.schoolColors)
      isInitialized.value = true
    } catch (error) {
      user.value = null
      token.value = null
      isInitialized.value = true
      throw error
    }
  }

  /**
   * Apply branding colors to CSS variables
   * DRAMATIC morphing - changes nav, buttons, badges, borders
   */
  function applyBranding(colors?: { primary: string; secondary: string } | null): void {
    const primary = colors?.primary || '#C9A961'
    const secondary = colors?.secondary || '#1a1a1a'

    // Core school colors
    document.documentElement.style.setProperty('--school-primary', primary)
    document.documentElement.style.setProperty('--school-secondary', secondary)

    // Primary accent (nav active, buttons, links)
    document.documentElement.style.setProperty('--color-accent-primary', primary)
    document.documentElement.style.setProperty('--color-accent-hover', primary)

    // Secondary accents
    document.documentElement.style.setProperty('--color-accent-secondary', secondary)

    // Interactive elements
    document.documentElement.style.setProperty('--color-button-primary', primary)
    document.documentElement.style.setProperty('--color-button-hover', primary)

    // Borders and highlights
    document.documentElement.style.setProperty('--color-border-focus', primary)
    document.documentElement.style.setProperty('--color-border-active', primary)
  }

  /**
   * Set role preview (SUPERADMIN only)
   * This MORPHS the entire app to show what that role would see
   */
  function setRolePreview(role: string | null): void {
    if (user.value?.userType !== 'SuperAdmin') return
    previewRole.value = role
  }

  /**
   * Set branding preview (SuperAdmin only)
   * This MORPHS the entire app's color scheme
   */
  function setBrandingPreview(branding: SchoolBranding | null): void {
    if (user.value?.userType !== 'SuperAdmin') return
    previewBranding.value = branding
    if (branding) {
      applyBranding({ primary: branding.primaryColor, secondary: branding.secondaryColor })
    } else {
      applyBranding(user.value?.schoolColors)
    }
  }

  /**
   * Clear all preview modes
   */
  function clearPreview(): void {
    previewRole.value = null
    previewBranding.value = null
    applyBranding(user.value?.schoolColors)
  }

  /**
   * Check if user has any of the required roles (uses effective role in preview)
   * IMPORTANT: SUPERADMIN actual role ALWAYS has access (can't lock out of Profile/Tools)
   */
  function hasAnyRole(roles: string[]): boolean {
    // SuperAdmin actual role always has access - prevents lockout during preview mode
    if (user.value?.userType === 'SuperAdmin') return true

    const roleToCheck = effectiveRole.value
    if (!roleToCheck) return false
    return roles.includes(roleToCheck)
  }

  /**
   * Logout
   */
  async function logout(): Promise<void> {
    try {
      await fetch('/api/admin/auth/logout', {
        method: 'POST',
        credentials: 'include'
      })
    } catch (error) {
      console.error('Logout failed:', error)
    } finally {
      user.value = null
      token.value = null
      window.location.href = '/api/admin/auth/logout'
    }
  }

  return {
    user,
    token,
    isAuthenticated,
    isInitialized,
    userRole,
    effectiveRole,
    isPreviewMode,
    previewRole,
    previewBranding,
    fetchCurrentUser,
    hasAnyRole,
    logout,
    setRolePreview,
    setBrandingPreview,
    clearPreview,
    applyBranding
  }
})
